import React, { useEffect, useMemo, useState } from "react";
import ScreenRecorder from ".";
import ApiService from "../../../service/ApiService";
import { apiPaths } from "../../../service/apiPaths";
import Modal from "../../../components/Model";
import VideoIcon from "../../../images/SideBar/Video";
import { TrashIcon } from "lucide-react";

type TaxonomyItem = { category: string; subcategories: string[] };

const CategoryRecorderPage: React.FC = () => {
  const [category, setCategory] = useState<string>("");
  const [subcategory, setSubcategory] = useState<string>("");
  const [taxonomy, setTaxonomy] = useState<TaxonomyItem[]>([]);
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<"recording" | "videos">("recording");
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [selectedVideo, setSelectedVideo] = useState<any | null>(null);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editingVideo, setEditingVideo] = useState<any | null>(null);
  const [editDescription, setEditDescription] = useState<string>("");

  const effectiveCategory = useMemo(() => category.trim(), [category]);
  const effectiveSubcategory = useMemo(() => subcategory.trim(), [subcategory]);
  const isAdmin = useMemo(() => (localStorage.getItem("role") || "").toLowerCase() === "admin", []);

  const categoriesList = useMemo(
    () => Array.from(new Set(taxonomy.map((t) => t.category).filter(Boolean))),
    [taxonomy]
  );
  const subcategoriesList = useMemo(() => {
    const found = taxonomy.find((t) => t.category === effectiveCategory);
    return found && Array.isArray(found.subcategories) ? found.subcategories : [];
  }, [taxonomy, effectiveCategory]);

  const mergeTaxonomy = (a: TaxonomyItem[], b: TaxonomyItem[]): TaxonomyItem[] => {
    const map = new Map<string, Set<string>>();
    [...a, ...b].forEach(({ category, subcategories }) => {
      const key = category || "";
      if (!map.has(key)) map.set(key, new Set<string>());
      const set = map.get(key)!;
      (subcategories || []).filter(Boolean).forEach((s) => set.add(s));
    });
    return Array.from(map.entries()).map(([category, subs]) => ({
      category,
      subcategories: Array.from(subs.values()).sort(),
    }));
  };

  const loadTaxonomy = async () => {
    try {
      // Prefer admin-managed categories first
      const categoriesResp = await ApiService({ method: "GET", endpoint: apiPaths.videoCategories });
      const managed: TaxonomyItem[] = Array.isArray(categoriesResp)
        ? categoriesResp.map((c: any) => ({ category: c.name, subcategories: c.subcategories || [] }))
        : [];
      // Merge with observed taxonomy from uploaded videos for discoverability
      const serverTaxonomy = await ApiService({ method: "GET", endpoint: apiPaths.videos_taxonomy });
      const observed: TaxonomyItem[] = Array.isArray(serverTaxonomy) ? serverTaxonomy : [];
      setTaxonomy(mergeTaxonomy(managed, observed));
    } catch (e) {
      // ignore, fallback to defaults
    }
  };

  const loadVideos = async () => {
    setLoading(true);
    try {
      const params: string[] = [];
      if (effectiveCategory) params.push(`category=${encodeURIComponent(effectiveCategory)}`);
      if (effectiveSubcategory) params.push(`subcategory=${encodeURIComponent(effectiveSubcategory)}`);

      // Attempt server-side filtering for non-admin users via common ownership param names
      const role = localStorage.getItem("role");
      const currentUserId = localStorage.getItem("userId") || "";
      if (role !== "admin" && currentUserId) {
        ["userId", "user", "ownerId", "createdBy", "uploadedBy"].forEach((key) => {
          params.push(`${key}=${encodeURIComponent(currentUserId)}`);
        });
      }

      const endpoint = params.length ? `${apiPaths.videos_list}?${params.join("&")}` : apiPaths.videos_list;
      const data = await ApiService({ method: "GET", endpoint });

      // Support multiple common API response shapes
      let list = Array.isArray(data)
        ? data
        : Array.isArray((data || {}).videos)
        ? data.videos
        : Array.isArray((data || {}).items)
        ? data.items
        : Array.isArray((data || {}).results)
        ? data.results
        : Array.isArray((data || {}).data)
        ? data.data
        : [];

      // Client-side fallback filtering in case backend ignores userId filter
      // Note: reusing role and currentUserId from above
      const currentUserEmail = (localStorage.getItem("userEmail") || localStorage.getItem("loginUsername") || "").toLowerCase();
      if (role !== "admin") {
        list = list.filter((v: any) => {
          const candidates: any[] = [
            v.userId,
            v.user?._id, v.user?.id, v.user,
            v.createdBy?._id, v.createdBy?.id, v.createdBy,
            v.uploadedBy?._id, v.uploadedBy?.id, v.uploadedBy,
            v.owner?._id, v.owner?.id, v.owner, v.ownerId,
            v.userEmail, v.email, v.username, v.userName, v.name,
          ].filter(Boolean);

          // Match by ID exactly
          const idMatch = candidates.some((c: any) => {
            const idVal = typeof c === "string" ? c : (c && (c._id || c.id));
            return !!idVal && String(idVal) === currentUserId;
          });

          if (idMatch) return true;

          // Match by email/username (case-insensitive)
          if (currentUserEmail) {
            const textMatch = candidates.some((c: any) => {
              const val = typeof c === "string" ? c : (c && (c.email || c.username || c.userName || c.name));
              return !!val && String(val).toLowerCase() === currentUserEmail;
            });
            if (textMatch) return true;
          }

          return false;
        });
      }

      setVideos(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTaxonomy();
  }, []);

  // When taxonomy loads, default to first category and its first subcategory (if any)
  useEffect(() => {
    if (!category && categoriesList.length > 0) {
      setCategory(categoriesList[0] || "");
    }
  }, [categoriesList]);

  useEffect(() => {
    if (!subcategory && subcategoriesList.length > 0) {
      setSubcategory(subcategoriesList[0] || "");
    }
  }, [subcategoriesList]);

  useEffect(() => {
    if (activeTab === "videos") {
      loadVideos();
    }
  }, [effectiveCategory, effectiveSubcategory, activeTab]);

  useEffect(() => {
    // Reset subcategory when category changes
    setSubcategory("");
  }, [effectiveCategory]);

  // Ensure defaults when opening the Videos tab: select first category and its first subcategory
  useEffect(() => {
    if (activeTab !== "videos") return;
    // Do not override when user selects All (empty category)
    if (category && !subcategory && subcategoriesList.length > 0) {
      setSubcategory(subcategoriesList[0] || "");
    }
  }, [activeTab, category, subcategoriesList, subcategory]);

  // Video preview modal handlers
  const openPreview = (v: any) => {
    setSelectedVideo(v);
    setIsPreviewOpen(true);
  };
  const closePreview = () => {
    setIsPreviewOpen(false);
    setSelectedVideo(null);
  };

  const deleteVideo = async (videoId: string) => {
    if (!isAdmin) return;
    await ApiService({ method: "DELETE", endpoint: `${apiPaths.videoDelete}${videoId}` });
    loadVideos();
  };

  const openEdit = (v: any) => {
    if (!isAdmin) return;
    setEditingVideo(v);
    setEditDescription(v?.description || "");
    setIsEditOpen(true);
  };

  const closeEdit = () => {
    setIsEditOpen(false);
    setEditingVideo(null);
    setEditDescription("");
  };

  const saveEdit = async () => {
    if (!isAdmin || !editingVideo) return;
    await ApiService({ method: "PUT", endpoint: `${apiPaths.videoEdit}${editingVideo._id}` , data: { description: editDescription } });
    closeEdit();
    loadVideos();
  };

  return (
    <div className="flex flex-col h-[85vh] p-4">
      {/* Tabs header */}
      <div className="flex border-b mb-4 flex-shrink-0">
        <button
          className={`px-4 py-2 -mb-px border-b-2 ${
            activeTab === "recording" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-600"
          }`}
          onClick={() => setActiveTab("recording")}
        >
          Recording
        </button>
        <button
          className={`ml-4 px-4 py-2 -mb-px border-b-2 ${
            activeTab === "videos" ? "border-blue-600 text-blue-600" : "border-transparent text-gray-600"
          }`}
          onClick={() => setActiveTab("videos")}
        >
          Videos
        </button>
      </div>

      {activeTab === "recording" && (
        <div className="flex-1 overflow-y-auto min-h-0">
          <div className="flex gap-4 mb-6 items-center w-96 ">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Select</option>
                {categoriesList.length === 0 && <option value="" disabled>No categories</option>}
                {categoriesList.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
              <select
                className="w-full border rounded px-3 py-2"
                value={subcategory}
                onChange={(e) => setSubcategory(e.target.value)}
                disabled={!effectiveCategory}
              >
                <option value="">Select</option>
                {subcategoriesList.length === 0 && <option value="" disabled>No subcategories</option>}
                {subcategoriesList.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-20 w-full h-[57vh]">
            <ScreenRecorder
              category={effectiveCategory || undefined}
              subcategory={effectiveSubcategory || undefined}
              onSave={() => {
                loadVideos();
                loadTaxonomy();
                setActiveTab("videos");
              }}
            />
          </div>
        </div>
      )}

      {activeTab === "videos" && (
        <div className="flex-1 overflow-y-auto min-h-0">
          <div>
            <h3 className="text-lg font-semibold mb-4">Saved Videos</h3>
            {/* Filters */}
            <div className="flex gap-4 mb-6 items-center w-96 ">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                >
                  <option value="">All</option>
                  {categoriesList.length === 0 && <option value="" disabled>No categories</option>}
                  {categoriesList.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Subcategory</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={subcategory}
                  onChange={(e) => setSubcategory(e.target.value)}
                  disabled={!effectiveCategory}
                >
                  <option value="">{effectiveCategory ? "All" : "Select category first"}</option>
                  {subcategoriesList.length === 0 && effectiveCategory && <option value="" disabled>No subcategories</option>}
                  {subcategoriesList.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
            </div>
            {loading ? (
              <div>Loading...</div>
            ) : videos.length === 0 ? (
              <div className="text-sm text-gray-500">No videos found</div>
            ) : (
              <div className="overflow-auto border rounded">
                <table className="min-w-full text-left">
                  <thead className="bg-gray-50 text-gray-700 text-sm">
                    <tr>
                      <th className="px-3 py-2">Index</th>
                      <th className="px-3 py-2">Category</th>
                      <th className="px-3 py-2">Subcategory</th>
                      <th className="px-3 py-2">Description</th>
                      <th className="px-3 py-2">User</th>
                      <th className="px-3 py-2">Preview</th>
                      {/* <th className="px-3 py-2">Created</th> */}
                      {isAdmin && <th className="px-3 py-2">Action</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {videos.map((v, idx) => (
                      <tr key={v._id}>
                        <td className="px-3 py-2 whitespace-nowrap">{idx + 1}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{v.category || "-"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">{v.subcategory || "-"}</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {v.description ? (
                            <span title={v.description} className="line-clamp-1 max-w-[240px] block">{v.description}</span>
                          ) : (
                            "-"
                          )}
                        </td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          {
                            (v.user && (v.user.name || v.user.fullName)) ||
                            v.userName ||
                            (v.uploadedBy && v.uploadedBy.name) ||
                            (v.createdBy && v.createdBy.name) ||
                            (v.userId && v.userId.name) ||
                            v.createdBy ||
                            v.user ||
                            "-"
                          }
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            onClick={() => openPreview(v)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
                            title="Open video"
                          >
                            <VideoIcon />
                            <span className="text-sm">Open</span>
                          </button>
                        </td>
                        {/* <td className="px-3 py-2 whitespace-nowrap">{new Date(v.createdAt).toLocaleString()}</td> */}
                        <td className="px-3 py-2 whitespace-nowrap">
                          {isAdmin && (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(v)}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
                                title="Edit description"
                              >
                                <span className="text-sm">Edit</span>
                              </button>
                              <button
                                type="button"
                                onClick={() => deleteVideo(v._id)}
                                className="inline-flex items-center gap-2 px-3 py-2 rounded border hover:bg-gray-50"
                                title="Delete video"
                              >
                                <TrashIcon />
                                <span className="text-sm">Delete</span>
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Video Preview Modal */}
            <Modal isOpen={isPreviewOpen} onClose={closePreview} style="w-[960px] max-w-[95vw] max-h-[85vh]">
              <div className="w-[900px] max-w-[90vw]">
                <h4 className="text-lg font-semibold mb-3">Video Preview</h4>
                {selectedVideo?.url ? (
                  <video
                    src={selectedVideo.url}
                    controls
                    playsInline
                    autoPlay
                    className="w-full h-[60vh] object-contain rounded"
                  />
                ) : (
                  <div className="text-sm text-gray-500">No video selected</div>
                )}
              </div>
            </Modal>
            <Modal isOpen={isEditOpen && isAdmin} onClose={closeEdit} style="w-[580px] max-w-[95vw]">
              <div className="w-[520px] max-w-[90vw]">
                <h4 className="text-lg font-semibold mb-3">Edit description</h4>
                <textarea
                  className="w-full border rounded px-3 py-2 min-h-[120px]"
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                />
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={closeEdit}
                    className="px-4 py-2 rounded border hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={saveEdit}
                    className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700"
                  >
                    Save
                  </button>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      )}
    </div>
  );
};

export default CategoryRecorderPage;


